import React,{useRef, useState} from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { countries, Country } from '../data/countries';
import contactRequestService from '../services/contactRequestService';
import { IMAGES } from '../layout/theme';

interface CountryOption {
    value: string;
    label: string;
    country: Country;
}

const TouchStart = () => {
    const { t } = useTranslation();
    const form = useRef<HTMLFormElement>(null);

    // Create options for react-select
    const countryOptions: CountryOption[] = countries.map(country => ({
        value: country.dialCode,
        label: `${country.flag} ${country.dialCode} - ${country.name}`,
        country: country
    }));

    // Custom format to show full name in dropdown, but short in selected value
    const formatOptionLabel = (option: CountryOption, { context }: { context: 'menu' | 'value' }) => {
        if (context === 'value') {
            // Show only flag and dial code when selected
            return `${option.country.flag} ${option.country.dialCode}`;
        }
        // Show full label in dropdown menu
        return option.label;
    };

    const [selectedCountry, setSelectedCountry] = useState<CountryOption>(countryOptions[0]);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<{[key: string]: string}>({});

    const handleCountryChange = (option: CountryOption | null) => {
        if (option) {
            setSelectedCountry(option);
        }
    };

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone: string): boolean => {
        // Remove spaces, dashes, parentheses
        const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
        // Check if it contains only digits and is between 6-15 characters
        return /^\d{6,15}$/.test(cleanPhone);
    };

	const sendEmail = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const target = e.target as HTMLFormElement;
		const formData = new FormData(target);

		// Extract form data
		const name = formData.get('dzName') as string;
		const lastName = formData.get('dzOther[last_name]') as string;
		const email = formData.get('dzEmail') as string;
		const phoneCode = formData.get('dzOther[phone_code]') as string;
		const phone = formData.get('dzOther[phone]') as string;
		const message = formData.get('dzMessage') as string;

		// Validation
		const newErrors: {[key: string]: string} = {};

		if (!validateEmail(email)) {
			newErrors.email = t('touchStart.errorInvalidEmail') || 'Please enter a valid email address (e.g., name@example.com)';
		}

		if (phone && !validatePhone(phone)) {
			newErrors.phone = t('touchStart.errorInvalidPhone') || 'Please enter a valid phone number (6-15 digits)';
		}

		if (!name.trim()) {
			newErrors.name = t('touchStart.errorRequiredField') || 'This field is required';
		}

		if (!lastName.trim()) {
			newErrors.lastName = t('touchStart.errorRequiredField') || 'This field is required';
		}

		if (!message.trim()) {
			newErrors.message = t('touchStart.errorRequiredField') || 'This field is required';
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			Swal.fire({
				title: t('touchStart.errorValidationTitle') || 'Validation Error',
				text: t('touchStart.errorValidationMessage') || 'Please check the form and correct the highlighted errors.',
				icon: "error",
				confirmButtonColor: '#d89d37'
			});
			return;
		}

		setErrors({});
		setSubmitting(true);
		try {
			// Create contact request
			await contactRequestService.create({
				name: name,
				last_name: lastName || undefined,
				email: email,
				subject: 'Get In Touch',
				message: message,
				indicative: phoneCode || undefined,
				number: phone || undefined,
				source: 'website'
			});

			// Reset form
			target.reset();
			setSelectedCountry(countryOptions[0]);

			// Show success message
			Swal.fire({
				title: t('touchStart.formSuccess') || 'Success!',
				text: t('touchStart.formSuccessText') || 'Thank you for contacting us! We will get back to you within 24 hours.',
				icon: "success",
				confirmButtonColor: '#d89d37'
			});
		} catch (error: any) {
			console.error('Error submitting contact request:', error);
			Swal.fire({
				title: t('touchStart.errorTitle') || 'Error',
				text: error?.response?.data?.error || t('touchStart.errorMessage') || 'Failed to submit your request. Please try again or contact us directly.',
				icon: "error",
				confirmButtonColor: '#d89d37'
			});
		} finally {
			setSubmitting(false);
		}
	};	
    return (
        <div id="contact" className="contact-card">
            {/* Left — form */}
            <div className="contact-card-form">
                <span className="sub-title contact-subtitle">{t('touchStart.subtitle')}</span>
                <h2 className="h2-title contact-heading">{t('touchStart.title')}</h2>
                <div className="contact-form">
                    <form className="dzForm" ref={form} onSubmit={sendEmail}>
                        <input type="hidden" name="dzToDo" value="Contact" />
                        <input type="hidden" name="reCaptchaEnable" value="0" />
                        <div className="contact-form-row">
                            <div className="contact-form-col contact-form-col-half">
                                <label htmlFor="dzName" className="contact-label">{t('touchStart.firstName')}</label>
                                <input
                                    id="dzName"
                                    name="dzName"
                                    type="text"
                                    required
                                    className={`form-input ${errors.name ? 'form-input-error' : ''}`}
                                    placeholder={t('touchStart.firstName')}
                                    onChange={() => setErrors(prev => ({...prev, name: ''}))}
                                />
                                {errors.name && <span className="form-error-message">{errors.name}</span>}
                            </div>
                            <div className="contact-form-col contact-form-col-half">
                                <label htmlFor="dzLastName" className="contact-label">{t('touchStart.lastName')}</label>
                                <input
                                    id="dzLastName"
                                    name="dzOther[last_name]"
                                    type="text"
                                    className={`form-input ${errors.lastName ? 'form-input-error' : ''}`}
                                    placeholder={t('touchStart.lastName')}
                                    required
                                    onChange={() => setErrors(prev => ({...prev, lastName: ''}))}
                                />
                                {errors.lastName && <span className="form-error-message">{errors.lastName}</span>}
                            </div>
                            <div className="contact-form-col">
                                <label htmlFor="dzEmail" className="contact-label">{t('touchStart.emailAddress')}</label>
                                <input
                                    id="dzEmail"
                                    type="email"
                                    name="dzEmail"
                                    className={`form-input ${errors.email ? 'form-input-error' : ''}`}
                                    placeholder={t('touchStart.emailAddress')}
                                    required
                                    onChange={() => setErrors(prev => ({...prev, email: ''}))}
                                />
                                {errors.email && <span className="form-error-message">{errors.email}</span>}
                            </div>
                            <div className="contact-form-col contact-form-col-phone">
                                <label className="contact-label">{t('touchStart.phoneNumber')}</label>
                                <div className="contact-phone-row">
                                    <input type="hidden" name="dzOther[phone_code]" value={selectedCountry.value} />
                                    <Select
                                        value={selectedCountry}
                                        onChange={handleCountryChange}
                                        options={countryOptions}
                                        formatOptionLabel={formatOptionLabel}
                                        isSearchable={true}
                                        placeholder={t('touchStart.selectCountryCode')}
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                height: '52px',
                                                minHeight: '52px',
                                                border: errors.phone ? '1px solid #dc3545' : '1px solid #c8d8dd',
                                                borderRadius: '8px',
                                                backgroundColor: '#ffffff',
                                                fontSize: '15px',
                                                color: '#2c3d4f',
                                                padding: '0 8px',
                                                boxShadow: 'none',
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                borderRadius: '8px',
                                                zIndex: 100,
                                                backgroundColor: '#ffffff',
                                                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                            }),
                                            option: (base, state) => ({
                                                ...base,
                                                backgroundColor: state.isSelected ? '#2c3d4f' : state.isFocused ? '#f0f4f8' : '#ffffff',
                                                color: state.isSelected ? '#ffffff' : '#2c3d4f',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                padding: '10px 12px',
                                            }),
                                            singleValue: (base) => ({
                                                ...base,
                                                color: '#2c3d4f',
                                            }),
                                            placeholder: (base) => ({
                                                ...base,
                                                color: '#999999',
                                            }),
                                            input: (base) => ({
                                                ...base,
                                                color: '#2c3d4f',
                                            }),
                                            indicatorSeparator: () => ({
                                                display: 'none',
                                            }),
                                            dropdownIndicator: (base) => ({
                                                ...base,
                                                color: '#2c3d4f',
                                            }),
                                        }}
                                    />
                                    <input
                                        type="tel"
                                        name="dzOther[phone]"
                                        className={`form-input ${errors.phone ? 'form-input-error' : ''}`}
                                        placeholder={t('touchStart.phoneNumber')}
                                        required
                                        onChange={() => setErrors(prev => ({...prev, phone: ''}))}
                                    />
                                </div>
                                {errors.phone && <span className="form-error-message">{errors.phone}</span>}
                            </div>
                            <div className="contact-form-col">
                                <label htmlFor="dzMessage" className="contact-label">{t('touchStart.message')}</label>
                                <textarea
                                    id="dzMessage"
                                    name="dzMessage"
                                    className={`form-input ${errors.message ? 'form-input-error' : ''}`}
                                    required
                                    placeholder={t('touchStart.message')}
                                    onChange={() => setErrors(prev => ({...prev, message: ''}))}
                                />
                                {errors.message && <span className="form-error-message">{errors.message}</span>}
                            </div>
                            <div className="contact-form-col">
                                <button name="submit" type="submit" value="Submit" className="sec-btn contact-submit" disabled={submitting}>
                                    <span>
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {t('touchStart.submitting') || 'Sending...'}
                                            </>
                                        ) : (
                                            t('touchStart.submitNow')
                                        )}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Right — info panel */}
            <div className="contact-card-info">
                <div className="contact-info-header">
                    <img src={IMAGES.iconOneKey} alt="Pure Key Homes" className="contact-logo-icon" />
                    <p className="contact-info-sub">{t('touchStart.panelSub')}</p>
                </div>
                <div className="contact-info-item">
                    <div className="contact-info-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d89d37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                    </div>
                    <div className="contact-info-text">
                        <span className="contact-info-label">{t('touchStart.panelPhone')}</span>
                        <a href="tel:+34614005838" className="contact-info-value">+34 614 005 838</a>
                    </div>
                </div>
                <div className="contact-info-item">
                    <div className="contact-info-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d89d37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    </div>
                    <div className="contact-info-text">
                        <span className="contact-info-label">{t('touchStart.panelEmail')}</span>
                        <a href="mailto:info@pure-key-homes.es" className="contact-info-value">info@pure-key-homes.es</a>
                    </div>
                </div>
                <div className="contact-info-item">
                    <div className="contact-info-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d89d37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    </div>
                    <div className="contact-info-text">
                        <span className="contact-info-label">{t('touchStart.panelLocation')}</span>
                        <span className="contact-info-value">{t('touchStart.panelLocationValue')}</span>
                    </div>
                </div>
                <div className="contact-info-item">
                    <div className="contact-info-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d89d37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    </div>
                    <div className="contact-info-text">
                        <span className="contact-info-label">{t('touchStart.panelHours')}</span>
                        <span className="contact-info-value">{t('touchStart.panelHoursValue')}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TouchStart;